import React, { useState, useEffect, useRef } from 'react';
import { Mail, Copy, Check, Plus, Menu, X, Smartphone, MapPin, Github, Linkedin, Facebook, ExternalLink } from 'lucide-react';
import SignalCanvas from './SignalCanvas';
import {
  person,
  languages,
  projectFilters,
  projects,
  skillGroups,
  translations,
  getInitialLanguage,
  saveLanguage,
} from '../data';

const navIds = ['work', 'career', 'skills', 'about'];

const socialLinks = [
  { label: 'LinkedIn', href: person.links.linkedin, Icon: Linkedin },
  { label: 'GitHub', href: person.links.github, Icon: Github },
  { label: 'Facebook', href: person.links.facebook, Icon: Facebook },
];

// Projects shown before the visitor asks for the full list
const PROJECT_PREVIEW_COUNT = 5;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

// A page of the site. It starts as a rounded card sitting on the colour of the
// previous page and expands to full width as it scrolls into view.
const Panel = ({ id, tone, after, className = '', children }) => (
  <div className={after === 'dark' ? 'bg-ink' : 'bg-paper'}>
    <section
      id={id}
      data-panel
      className={`panel px-5 py-20 md:px-10 md:py-28 ${
        tone === 'dark' ? 'panel-dark text-paper' : 'bg-paper text-ink'
      } ${className}`}
    >
      {children}
    </section>
  </div>
);

const LanguageSwitch = ({ current, onChange, label }) => (
  <div role="group" aria-label={label} className="flex rounded-full border border-white/[0.15] p-0.5">
    {languages.map(({ code, label: name }) => (
      <button
        key={code}
        type="button"
        lang={code}
        onClick={() => onChange(code)}
        aria-pressed={current === code}
        className={`rounded-full px-2.5 py-2.5 text-[13px] font-medium leading-none transition-colors ${
          current === code ? 'bg-white text-ink' : 'text-paper/60 hover:text-white'
        }`}
      >
        {name}
      </button>
    ))}
  </div>
);

const PortfolioWebsite = () => {
  const [language, setLanguage] = useState(getInitialLanguage);
  const [activeSection, setActiveSection] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [openProject, setOpenProject] = useState(null);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [openExperience, setOpenExperience] = useState(null);
  const [openAbout, setOpenAbout] = useState(null);
  const [emailCopied, setEmailCopied] = useState(false);
  const heroRef = useRef(null);

  const t = translations[language];
  const { ui } = t;

  // Keep the document in step with the chosen language
  useEffect(() => {
    document.documentElement.lang = language;
    document.title = t.meta.title;
    const description = document.querySelector('meta[name="description"]');
    if (description) description.setAttribute('content', t.meta.description);
  }, [language, t]);

  const changeLanguage = (code) => {
    setLanguage(code);
    saveLanguage(code);
  };

  // Escape closes the mobile menu
  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  // Drive the scroll transitions: the hero zoom and each panel's expansion
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const hero = heroRef.current;
    const panels = Array.from(document.querySelectorAll('[data-panel]'));
    let frame = 0;

    const update = () => {
      frame = 0;
      const viewport = window.innerHeight;
      const heroScroll = hero.offsetHeight - viewport;
      const zoom = heroScroll > 0 ? clamp01(window.scrollY / heroScroll) : 0;
      hero.style.setProperty('--zoom', zoom.toFixed(4));
      hero.toggleAttribute('data-zoomed', zoom > 0.32);

      panels.forEach((panel) => {
        const top = panel.parentElement.getBoundingClientRect().top;
        const enter = clamp01((viewport - top) / (viewport * 0.9));
        panel.style.setProperty('--enter', enter.toFixed(4));
      });
    };

    const requestUpdate = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, []);

  // Highlight the section currently in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-40% 0px -55% 0px' }
    );
    navIds.forEach((id) => {
      const section = document.getElementById(id);
      if (section) observer.observe(section);
    });
    return () => observer.disconnect();
  }, []);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(person.email);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2500);
    } catch (error) {
      window.location.href = `mailto:${person.email}`;
    }
  };

  const visibleProjects = projects.filter(
    (project) => filter === 'all' || project.domains.includes(filter)
  );

  const shownProjects = showAllProjects ? visibleProjects : visibleProjects.slice(0, PROJECT_PREVIEW_COUNT);

  const aboutGroups = [
    {
      id: 'education',
      title: ui.education,
      content: (
        <ul className="grid gap-6 md:grid-cols-3 md:gap-10">
          {t.education.map((item) => (
            <li key={item.detail}>
              <p className="font-semibold leading-snug">{item.detail}</p>
              <p className="mt-1 text-mist">{item.school}</p>
              <p className="text-mist tabular-nums">{item.period}</p>
            </li>
          ))}
        </ul>
      ),
    },
    {
      id: 'activities',
      title: ui.activities,
      content: (
        <ul className="max-w-3xl space-y-3 leading-relaxed">
          {t.activities.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ),
    },
    {
      id: 'interests',
      title: ui.interests,
      content: (
        <ul className="max-w-3xl space-y-3 leading-relaxed">
          {t.interests.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-ink font-sans text-paper antialiased">
      {/* Navbar */}
      <header className="fixed inset-x-3 top-3 z-50 md:inset-x-6 md:top-4">
        <div
          className={`mx-auto max-w-5xl border border-white/[0.15] bg-ink/[0.85] text-paper shadow-[0_8px_30px_rgba(0,0,0,0.25)] backdrop-blur-xl ${
            isMenuOpen ? 'rounded-[28px]' : 'rounded-full'
          }`}
        >
          <nav className="flex h-14 items-center justify-between gap-3 pl-6 pr-2" aria-label={ui.mainNav}>
            <a href="#top" lang="en" className="py-2 font-display text-xl font-bold tracking-tight">
              {person.firstName}
            </a>

            <div className="hidden items-center gap-7 lg:flex">
              {navIds.map((id) => (
                <a
                  key={id}
                  href={`#${id}`}
                  aria-current={activeSection === id ? 'true' : undefined}
                  className={`text-[15px] font-medium transition-colors hover:text-white ${
                    activeSection === id ? 'text-white' : 'text-paper/60'
                  }`}
                >
                  {ui.nav[id]}
                </a>
              ))}
              <LanguageSwitch current={language} onChange={changeLanguage} label={ui.language} />
              <a
                href={`mailto:${person.email}`}
                className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[15px] font-semibold text-ink transition-colors hover:bg-silver"
              >
                <Mail className="h-4 w-4" aria-hidden="true" />
                {ui.emailMe}
              </a>
            </div>

            <div className="flex items-center gap-1 lg:hidden">
              <LanguageSwitch current={language} onChange={changeLanguage} label={ui.language} />
              <button
                type="button"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
                className="p-2"
              >
                <span className="sr-only">{isMenuOpen ? ui.closeMenu : ui.openMenu}</span>
                {isMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
              </button>
            </div>
          </nav>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div id="mobile-menu" className="border-t border-white/[0.15] px-6 pb-6 pt-2 lg:hidden">
              {navIds.map((id) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={() => setIsMenuOpen(false)}
                  className="block py-3 font-display text-2xl font-bold"
                >
                  {ui.nav[id]}
                </a>
              ))}
              <a
                href={`mailto:${person.email}`}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-ink"
              >
                <Mail className="h-4 w-4" aria-hidden="true" />
                {ui.emailMe}
              </a>
            </div>
          )}
        </div>
      </header>

      <main id="top">
        {/* Hero Section: stays pinned while the name zooms in, then gives way to the next page */}
        <div ref={heroRef} className="hero-wrap">
          <section className="hero-surface sticky top-0 flex h-[100svh] flex-col overflow-hidden pt-20 text-paper md:pt-28">
            <div className="hero-fade px-5 md:px-10 lg:flex lg:items-end lg:justify-between lg:gap-10">
              <p className="hero-intro max-w-2xl leading-relaxed text-paper/[0.85] md:leading-snug">
                {t.person.intro}
              </p>
              <div className="mt-5 flex flex-wrap gap-3 lg:mt-0 lg:flex-shrink-0">
                <a
                  href={`mailto:${person.email}`}
                  className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold text-ink transition-colors hover:bg-silver"
                >
                  <Mail className="h-5 w-5" aria-hidden="true" />
                  {ui.emailMe}
                </a>
                <a
                  href="#work"
                  className="hidden items-center rounded-full border border-white/40 px-7 py-3.5 text-base font-semibold transition-colors hover:bg-white hover:text-ink sm:inline-flex"
                >
                  {ui.seeWork}
                </a>
              </div>
            </div>

            <SignalCanvas className="hero-fade my-4 min-h-[70px] flex-1 md:my-8" labels={ui.signal} />

            <h1 lang="en" className="hero-name name-display px-5 pb-4 md:px-10 md:pb-6">
              <span className="name-line">
                <span className="text-fade-light">{person.firstName}</span>
              </span>
              <span className="name-line">
                <span className="text-fade-light">{person.lastName}</span>
              </span>
            </h1>

            <div className="hero-flash pointer-events-none absolute inset-0 bg-paper" aria-hidden="true" />
          </section>
        </div>

        {/* Proof Section */}
        <section className="bg-paper px-5 py-20 text-ink md:px-10 md:py-28" aria-label={ui.highlights}>
          <div className="grid gap-10 md:grid-cols-12 md:gap-12">
            <p className="text-fade-dark font-display text-4xl font-bold leading-[1.05] tracking-tight md:col-span-7 md:text-6xl">
              {t.proof.lead}
            </p>
            <div className="space-y-6 md:col-span-5 md:pt-2">
              {t.proof.supporting.map((line) => (
                <p key={line} className="border-l-2 border-ink pl-5 text-lg leading-relaxed text-ink/80">
                  {line}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Work Section */}
        <Panel id="work" tone="dark" after="light">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <h2 className="text-fade-light font-display text-5xl font-extrabold tracking-tight md:text-7xl">{ui.headings.work}</h2>
            <div className="flex flex-wrap gap-2" role="group" aria-label={ui.filterLabel}>
              {projectFilters.map((id) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setFilter(id)}
                  aria-pressed={filter === id}
                  className={`rounded-full border px-5 py-2.5 text-[15px] font-medium transition-colors ${
                    filter === id
                      ? 'border-white bg-white text-ink'
                      : 'border-white/25 text-paper hover:border-white'
                  }`}
                >
                  {t.filters[id]}
                </button>
              ))}
            </div>
          </div>

          <ul className="mt-10 space-y-3 md:mt-14">
            {shownProjects.map((project) => {
              const isOpen = openProject === project.id;
              const text = t.projects[project.id];
              return (
                <li
                  key={project.id}
                  className={`rounded-[28px] border transition-colors duration-300 ${
                    isOpen ? 'border-white bg-paper text-ink' : 'border-white/10 bg-white/5 text-paper hover:bg-white/10'
                  }`}
                >
                  <h3>
                    <button
                      type="button"
                      onClick={() => setOpenProject(isOpen ? null : project.id)}
                      aria-expanded={isOpen}
                      aria-controls={`project-${project.id}`}
                      className="flex w-full items-start gap-5 rounded-[28px] p-6 text-left md:gap-8 md:p-9"
                    >
                      <span className="flex-1">
                        <span className="block font-display text-2xl font-bold leading-tight tracking-tight md:text-4xl">
                          {text.title}
                        </span>
                        <span
                          className={`mt-3 max-w-3xl text-base font-normal leading-relaxed opacity-75 md:text-lg ${
                            isOpen ? 'block' : 'hidden md:block'
                          }`}
                        >
                          {text.summary}
                        </span>
                        {text.place && (
                          <span className={`mt-3 block text-[15px] font-semibold ${isOpen ? 'text-ink' : 'text-silver'}`}>
                            {text.place}
                          </span>
                        )}
                      </span>
                      <Plus
                        className={`mt-1 h-7 w-7 flex-shrink-0 transition-transform duration-300 md:h-9 md:w-9 ${
                          isOpen ? 'rotate-45' : ''
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </h3>
                  {isOpen && (
                    <div id={`project-${project.id}`} className="px-6 pb-7 md:px-9 md:pb-10">
                      <p className="max-w-3xl border-t border-ink/[0.15] pt-6 text-base leading-relaxed text-ink/80 md:text-lg">
                        {text.details}
                      </p>
                      {project.stack.length > 0 && (
                        <ul className="mt-6 flex flex-wrap gap-2" aria-label={ui.techUsed}>
                          {project.stack.map((item) => (
                            <li key={item} lang="en" className="rounded-full border border-ink/25 px-4 py-1.5 text-sm">
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}
                      {project.url && (
                        <a
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-[15px] font-semibold text-paper transition-colors hover:bg-graphite"
                        >
                          {ui.visit} {project.urlLabel}
                          <ExternalLink className="h-4 w-4" aria-hidden="true" />
                        </a>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {visibleProjects.length > PROJECT_PREVIEW_COUNT && (
            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => setShowAllProjects(!showAllProjects)}
                aria-expanded={showAllProjects}
                className="rounded-full border border-white/40 px-7 py-3.5 text-base font-semibold transition-colors hover:bg-white hover:text-ink"
              >
                {showAllProjects ? ui.showFewerProjects : ui.showAllProjects(visibleProjects.length)}
              </button>
            </div>
          )}
        </Panel>

        {/* Career Section */}
        <Panel id="career" tone="light" after="dark">
          <h2 className="text-fade-dark font-display text-5xl font-extrabold tracking-tight md:text-7xl">{ui.headings.career}</h2>

          <ol className="mt-10 md:mt-14">
            {t.experiences.map((exp) => (
              <li key={exp.id} className="grid gap-4 border-t border-line py-10 md:grid-cols-12 md:gap-10 md:py-14">
                <p className="text-[15px] font-semibold tabular-nums text-mist md:col-span-3 md:pt-2">{exp.period}</p>
                <div className="md:col-span-9">
                  <h3 className="font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">{exp.role}</h3>
                  <p className="mt-1 text-lg font-medium text-mist">{exp.company}</p>
                  <p className="mt-5 max-w-3xl text-lg leading-relaxed">{exp.description}</p>
                  {exp.achievements.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setOpenExperience(openExperience === exp.id ? null : exp.id)}
                        aria-expanded={openExperience === exp.id}
                        aria-controls={`experience-${exp.id}`}
                        className="mt-5 inline-flex items-center gap-2 rounded-full border border-ink/25 px-5 py-2.5 text-[15px] font-semibold transition-colors hover:border-ink"
                      >
                        {openExperience === exp.id ? ui.hideDetails : ui.showDetails}
                        <Plus
                          className={`h-4 w-4 transition-transform duration-300 ${openExperience === exp.id ? 'rotate-45' : ''}`}
                          aria-hidden="true"
                        />
                      </button>
                      {openExperience === exp.id && (
                        <ul
                          id={`experience-${exp.id}`}
                          className="mt-5 max-w-3xl list-disc space-y-2 pl-5 leading-relaxed text-ink/80 marker:text-steel"
                        >
                          {exp.achievements.map((achievement) => (
                            <li key={achievement}>{achievement}</li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </Panel>

        {/* Skills Section */}
        <Panel id="skills" tone="dark" after="light">
          <h2 className="text-fade-light font-display text-5xl font-extrabold tracking-tight md:text-7xl">{ui.headings.skills}</h2>

          <dl className="mt-10 md:mt-14">
            {skillGroups.map((group) => (
              <div key={group.id} className="grid gap-4 border-t border-white/10 py-7 md:grid-cols-12 md:gap-10 md:py-9">
                <dt className="font-display text-2xl font-bold tracking-tight md:col-span-4 md:text-3xl">{t.skillGroups[group.id]}</dt>
                <dd className="flex flex-wrap gap-2 md:col-span-8">
                  {group.skills.map((skill) => (
                    <span key={skill} lang="en" className="rounded-full border border-white/[0.15] bg-white/5 px-5 py-2.5 text-base font-medium">
                      {skill}
                    </span>
                  ))}
                </dd>
              </div>
            ))}
          </dl>
        </Panel>

        {/* About Section */}
        <Panel id="about" tone="light" after="dark">
          <h2 className="text-fade-dark font-display text-5xl font-extrabold tracking-tight md:text-7xl">{ui.headings.about}</h2>

          <div className="mt-10 grid gap-8 rounded-[28px] bg-ink p-7 text-paper md:mt-14 md:grid-cols-12 md:gap-10 md:p-10">
            <div className="md:col-span-5">
              <h3 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{t.markets.title}</h3>
              <p className="mt-4 text-lg leading-relaxed text-paper/80">{t.markets.lead}</p>
            </div>
            <dl className="grid gap-6 sm:grid-cols-2 md:col-span-7">
              {t.markets.points.map((point) => (
                <div key={point.term}>
                  <dt className="font-semibold">{point.term}</dt>
                  <dd className="mt-1 leading-relaxed text-paper/60">{point.text}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="mt-10 md:mt-14">
            {aboutGroups.map((group) => {
              const isOpen = openAbout === group.id;
              return (
                <div key={group.id} className="border-t border-line last:border-b">
                  <h3>
                    <button
                      type="button"
                      onClick={() => setOpenAbout(isOpen ? null : group.id)}
                      aria-expanded={isOpen}
                      aria-controls={`about-${group.id}`}
                      className="flex w-full items-center justify-between gap-5 py-6 text-left font-display text-2xl font-bold tracking-tight md:text-3xl"
                    >
                      {group.title}
                      <Plus
                        className={`h-7 w-7 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}
                        aria-hidden="true"
                      />
                    </button>
                  </h3>
                  {isOpen && (
                    <div id={`about-${group.id}`} className="pb-8">
                      {group.content}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>

        {/* Contact Section */}
        <Panel id="contact" tone="dark" after="light">
          <h2 className="text-fade-light max-w-5xl font-display text-5xl font-extrabold leading-[0.95] tracking-tight md:text-8xl">
            {ui.headings.contact}
          </h2>

          <a
            href={`mailto:${person.email}`}
            lang="en"
            className="mt-10 block break-all font-display text-2xl font-bold underline decoration-white/40 decoration-2 underline-offset-8 hover:decoration-white md:mt-14 md:text-5xl"
          >
            {person.email}
          </a>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`mailto:${person.email}`}
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold text-ink transition-colors hover:bg-silver"
            >
              <Mail className="h-5 w-5" aria-hidden="true" />
              {ui.emailMe}
            </a>
            <button
              type="button"
              onClick={copyEmail}
              className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-base font-semibold transition-colors hover:bg-white hover:text-ink"
            >
              {emailCopied ? <Check className="h-5 w-5" aria-hidden="true" /> : <Copy className="h-5 w-5" aria-hidden="true" />}
              <span aria-live="polite">{emailCopied ? ui.emailCopied : ui.copyEmail}</span>
            </button>
          </div>

          <div className="mt-14 flex flex-col gap-6 border-t border-white/[0.15] pt-8 text-lg md:flex-row md:flex-wrap md:items-center md:gap-10">
            <a href={person.phoneHref} className="inline-flex items-center gap-3 py-2 text-paper/80 hover:text-white">
              <Smartphone className="h-5 w-5" aria-hidden="true" />
              {person.phone}
            </a>
            <span className="inline-flex items-center gap-3 text-paper/80">
              <MapPin className="h-5 w-5" aria-hidden="true" />
              {t.person.location}
            </span>
            <div className="flex flex-wrap gap-6 md:ml-auto">
              {socialLinks.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  lang="en"
                  className="inline-flex items-center gap-2 py-2 text-paper/80 hover:text-white"
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                  {label}
                </a>
              ))}
            </div>
          </div>
        </Panel>
      </main>

      {/* Footer */}
      <footer className="bg-ink px-5 py-8 text-paper/60 md:px-10">
        <p lang="en">
          &copy; {new Date().getFullYear()} {person.firstName} {person.lastName}
        </p>
      </footer>
    </div>
  );
};

export default PortfolioWebsite;
